
import { supabase } from './supabase';

export interface Episode {
  id?: number;
  title: string;
  description: string;
  guest: string;
  guestLinkedin: string;
  guest2?: string;
  guestLinkedin2?: string;
  guest3?: string;
  guestLinkedin3?: string;
  guest4?: string;
  guestLinkedin4?: string;
  guest5?: string;
  guestLinkedin5?: string;
  notes?: string;
  showNotes?: string;
  transcript?: string;
  resource1Name?: string;
  resource1Url?: string;
  resource2Name?: string;
  resource2Url?: string;
  resource3Name?: string;
  resource3Url?: string;
  relatedEpisodes?: number[];
  category: string;
  category2?: string;
  category3?: string;
  category4?: string;
  category5?: string;
  audioUrl: string;
  videoUrl?: string;
  video_url?: string;
  media_type?: 'audio' | 'video';
  duration?: string;
  advertisingImage?: string;
  date: string;
  viewCount?: number;
  visible?: boolean;
  status?: 'Published' | 'Draft';
  on_focus?: boolean;
  spotify_url?: string;
  youtube_url?: string;
}

export interface Category {
  id?: number;
  name: string;
  description?: string;
  createdDate?: string;
}

export interface Industry {
  id?: number;
  name: string;
  description?: string;
  createdDate?: string;
}

export interface Subscriber {
  id?: number;
  name: string;
  email: string;
  country: string;
  industry?: string;
  subscriptionDate?: string; // ISO datetime string when user subscribed
  registrationDate?: string;
  lastAccess?: string;
  totalViews: number;
  episodeViews: { [key: number]: number };
}

export interface EpisodeView {
  episodeId: number;
  subscriberId?: number;
  viewDate: string;
}

// Database interface for easy switching between storage methods
class Database {
  private useSupabase = false;
  private connectionChecked = false;
  private connectionPromise: Promise<void> | null = null;

  constructor() {
    // Don't call async function in constructor
  }

  private async ensureConnection(): Promise<void> {
    if (this.connectionChecked) return;
    
    // Prevent multiple simultaneous connection attempts
    if (this.connectionPromise) {
      return this.connectionPromise;
    }
    
    this.connectionPromise = this.checkConnection();
    await this.connectionPromise;
  }

  private async checkConnection(): Promise<void> {
    try {
      // Only check connection if we have valid environment variables
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey || supabaseUrl.includes('placeholder')) {
        console.log('Using localStorage for data storage - Supabase not configured');
        this.connectionChecked = true;
        return;
      }
      
      const { data, error } = await supabase.from('episodes').select('count').limit(1);
      if (!error) {
        this.useSupabase = true;
        console.log('Using Supabase for data storage');
      } else {
        console.log('Using localStorage for data storage - Supabase connection failed', error);
      }
    } catch (e) {
      console.log('Using localStorage for data storage - Supabase unavailable', e);
    }
    
    this.connectionChecked = true;
  }

  // Episodes CRUD operations
  async getEpisodes(): Promise<Episode[]> {
    try {
      await this.ensureConnection();
      
      if (this.useSupabase) {
        try {
          const { data, error } = await supabase
            .from('episodes')
            .select('*')
            .order('date', { ascending: false });
          
          if (error) {
            console.error('Supabase error:', error);
            throw error;
          }
          
          console.log('Retrieved episodes from Supabase:', data?.length || 0);
          return data?.map(this.mapSupabaseEpisode) || [];
        } catch (error) {
          console.error('Failed to fetch from Supabase, falling back to localStorage:', error);
          // Fallback to localStorage if Supabase fails
          const episodes = localStorage.getItem('dbd_episodes');
          return episodes ? JSON.parse(episodes) : this.getDefaultEpisodes();
        }
      } else {
        const episodes = localStorage.getItem('dbd_episodes');
        return episodes ? JSON.parse(episodes) : this.getDefaultEpisodes();
      }
    } catch (error) {
      console.error('Error in getEpisodes:', error);
      // Return default episodes as final fallback
      return this.getDefaultEpisodes();
    }
  }

  async getEpisode(id: number): Promise<Episode | null> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('episodes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) return null;
      return this.mapSupabaseEpisode(data);
    } else {
      const episodes = await this.getEpisodes();
      return episodes.find(ep => ep.id === id) || null;
    }
  }

  async createEpisode(episode: Omit<Episode, 'id' | 'viewCount' | 'createdAt' | 'updatedAt'>): Promise<Episode> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('episodes')
        .insert([this.mapToSupabaseEpisode({ ...episode, viewCount: 0 })])
        .select()
        .single();
      
      if (error) throw error;
      return this.mapSupabaseEpisode(data);
    } else {
      const episodes = await this.getEpisodes();
      const newEpisode: Episode = {
        ...episode,
        id: Math.max(...episodes.map(ep => ep.id || 0), 0) + 1,
        viewCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      episodes.push(newEpisode);
      localStorage.setItem('dbd_episodes', JSON.stringify(episodes));
      return newEpisode;
    }
  }

  async updateEpisode(id: number, updates: Partial<Episode>): Promise<Episode> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('episodes')
        .update(this.mapToSupabaseEpisode({ ...updates, updatedAt: new Date().toISOString() }))
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return this.mapSupabaseEpisode(data);
    } else {
      const episodes = await this.getEpisodes();
      const index = episodes.findIndex(ep => ep.id === id);
      if (index === -1) throw new Error('Episode not found');
      
      episodes[index] = { ...episodes[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem('dbd_episodes', JSON.stringify(episodes));
      return episodes[index];
    }
  }

  async incrementEpisodeViews(episodeId: number): Promise<void> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      await supabase.rpc('increment_episode_views', { episode_id: episodeId });
    } else {
      const episodes = await this.getEpisodes();
      const episode = episodes.find(ep => ep.id === episodeId);
      if (episode) {
        episode.viewCount = (episode.viewCount || 0) + 1;
        localStorage.setItem('dbd_episodes', JSON.stringify(episodes));
      }
    }
  }

  // Categories CRUD operations
  async getCategories(): Promise<Category[]> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data || [];
    } else {
      const categories = localStorage.getItem('dbd_categories');
      return categories ? JSON.parse(categories) : this.getDefaultCategories();
    }
  }

  async createCategory(category: Omit<Category, 'id' | 'createdDate'>): Promise<Category> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('categories')
        .insert([category])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      const categories = await this.getCategories();
      const newCategory: Category = {
        ...category,
        id: Math.max(...categories.map(c => c.id || 0), 0) + 1,
        createdDate: new Date().toISOString().split('T')[0]
      };
      categories.push(newCategory);
      localStorage.setItem('dbd_categories', JSON.stringify(categories));
      return newCategory;
    }
  }

  async updateCategory(id: number, updates: Partial<Category>): Promise<Category> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      const categories = await this.getCategories();
      const index = categories.findIndex(c => c.id === id);
      if (index === -1) throw new Error('Category not found');
      
      categories[index] = { ...categories[index], ...updates };
      localStorage.setItem('dbd_categories', JSON.stringify(categories));
      return categories[index];
    }
  }

  async deleteCategory(id: number): Promise<void> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } else {
      const categories = await this.getCategories();
      const filtered = categories.filter(c => c.id !== id);
      localStorage.setItem('dbd_categories', JSON.stringify(filtered));
    }
  }

  // Industries CRUD operations
  async getIndustries(): Promise<Industry[]> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('industries')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data || [];
    } else {
      const industries = localStorage.getItem('dbd_industries');
      return industries ? JSON.parse(industries) : this.getDefaultIndustries();
    }
  }

  async createIndustry(industry: Omit<Industry, 'id' | 'createdDate'>): Promise<Industry> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('industries')
        .insert([industry])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      const industries = await this.getIndustries();
      const newIndustry: Industry = {
        ...industry,
        id: Math.max(...industries.map(i => i.id || 0), 0) + 1,
        createdDate: new Date().toISOString().split('T')[0]
      };
      industries.push(newIndustry);
      localStorage.setItem('dbd_industries', JSON.stringify(industries));
      return newIndustry;
    }
  }

  async updateIndustry(id: number, updates: Partial<Industry>): Promise<Industry> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { data, error } = await supabase
        .from('industries')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      const industries = await this.getIndustries();
      const index = industries.findIndex(i => i.id === id);
      if (index === -1) throw new Error('Industry not found');
      
      industries[index] = { ...industries[index], ...updates };
      localStorage.setItem('dbd_industries', JSON.stringify(industries));
      return industries[index];
    }
  }

  async deleteIndustry(id: number): Promise<void> {
    await this.ensureConnection();
    
    if (this.useSupabase) {
      const { error } = await supabase
        .from('industries')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } else {
      const industries = await this.getIndustries();
      const filtered = industries.filter(i => i.id !== id);
      localStorage.setItem('dbd_industries', JSON.stringify(filtered));
    }
  }

  // Data export/import functions
  async exportAllData(): Promise<string> {
    const [episodes, categories, industries] = await Promise.all([
      this.getEpisodes(),
      this.getCategories(),
      this.getIndustries()
    ]);

    const exportData = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      data: {
        episodes,
        categories,
        industries
      }
    };

    return JSON.stringify(exportData, null, 2);
  }

  async importAllData(jsonData: string): Promise<void> {
    await this.ensureConnection();
    
    try {
      const importData = JSON.parse(jsonData);
      
      if (this.useSupabase) {
        // For Supabase, we'd need to clear and re-insert data
        // This is a simplified version - in production, you'd want more sophisticated merging
        await supabase.from('episodes').delete().neq('id', 0);
        await supabase.from('categories').delete().neq('id', 0);
        await supabase.from('industries').delete().neq('id', 0);

        if (importData.data.categories) {
          await supabase.from('categories').insert(importData.data.categories);
        }
        if (importData.data.industries) {
          await supabase.from('industries').insert(importData.data.industries);
        }
        if (importData.data.episodes) {
          await supabase.from('episodes').insert(importData.data.episodes.map(this.mapToSupabaseEpisode));
        }
      } else {
        localStorage.setItem('dbd_episodes', JSON.stringify(importData.data.episodes || []));
        localStorage.setItem('dbd_categories', JSON.stringify(importData.data.categories || []));
        localStorage.setItem('dbd_industries', JSON.stringify(importData.data.industries || []));
      }
    } catch (error) {
      throw new Error('Invalid import data format');
    }
  }

  // Mapping functions for Supabase compatibility
  private mapSupabaseEpisode(data: any): Episode {
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      guest: data.guest,
      guestLinkedin: data.guest_linkedin,
      guest2: data.guest2,
      guestLinkedin2: data.guest_linkedin2,
      guest3: data.guest3,
      guestLinkedin3: data.guest_linkedin3,
      guest4: data.guest4,
      guestLinkedin4: data.guest_linkedin4,
      guest5: data.guest5,
      guestLinkedin5: data.guest_linkedin5,
      notes: data.notes,
      showNotes: data.show_notes,
      transcript: data.transcript,
      resource1Name: data.resource1_name,
      resource1Url: data.resource1_url,
      resource2Name: data.resource2_name,
      resource2Url: data.resource2_url,
      resource3Name: data.resource3_name,
      resource3Url: data.resource3_url,
      relatedEpisodes: data.related_episodes ? JSON.parse(data.related_episodes) : [],
      category: data.category,
      category2: data.category2,
      category3: data.category3,
      category4: data.category4,
      category5: data.category5,
      audioUrl: data.audio_url,
      duration: data.duration,
      advertisingImage: data.advertising_image,
      date: data.date,
      viewCount: data.view_count || 0,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      status: data.status,
      visible: data.visible,
      on_focus: data.on_focus,
      spotify_url: data.spotify_url,
      youtube_url: data.youtube_url
    };
  }

  private mapToSupabaseEpisode(episode: Partial<Episode>): any {
    return {
      title: episode.title,
      description: episode.description,
      guest: episode.guest,
      guest_linkedin: episode.guestLinkedin,
      guest2: episode.guest2,
      guest_linkedin2: episode.guestLinkedin2,
      guest3: episode.guest3,
      guest_linkedin3: episode.guestLinkedin3,
      guest4: episode.guest4,
      guest_linkedin4: episode.guestLinkedin4,
      guest5: episode.guest5,
      guest_linkedin5: episode.guestLinkedin5,
      notes: episode.notes,
      show_notes: episode.showNotes,
      transcript: episode.transcript,
      resource1_name: episode.resource1Name,
      resource1_url: episode.resource1Url,
      resource2_name: episode.resource2Name,
      resource2_url: episode.resource2Url,
      resource3_name: episode.resource3Name,
      resource3_url: episode.resource3Url,
      related_episodes: episode.relatedEpisodes ? JSON.stringify(episode.relatedEpisodes) : null,
      category: episode.category,
      category2: episode.category2,
      category3: episode.category3,
      category4: episode.category4,
      category5: episode.category5,
      audio_url: episode.audioUrl,
      duration: episode.duration,
      advertising_image: episode.advertisingImage,
      date: episode.date,
      view_count: episode.viewCount,
      status: episode.status,
      visible: episode.visible,
      on_focus: episode.on_focus,
      // Additional fields for new interface (optional, may be undefined)
      video_url: episode.video_url,
      video_url: episode.videoUrl,
      media_type: episode.media_type,
      spotify_url: episode.spotify_url,
      youtube_url: episode.youtube_url
    };
  }

  private getDefaultEpisodes(): Episode[] {
    return [
      {
        id: 1,
        title: 'The Psychology of Business Leadership',
        description: 'Exploring cognitive biases in executive decision-making and how successful entrepreneurs balance calculated risks with intuitive decision-making in the spirits industry.',
        guest: 'Dr. Amanda Foster',
        guestLinkedin: 'https://linkedin.com/in/amanda-foster',
        notes: 'Key insights on leadership psychology and decision-making frameworks',
        showNotes: '• Introduction and guest welcome\n• Psychology of decision-making in business\n• Cognitive biases that affect leaders\n• Risk assessment strategies\n• Case studies from the spirits industry\n• Practical frameworks for better decisions\n• Q&A with listeners',
        transcript: 'Welcome to the DBD Podcast. Today we have Dr. Amanda Foster, a renowned business psychologist who specializes in executive decision-making...',
        resource1Name: 'Thinking, Fast and Slow by Daniel Kahneman',
        resource1Url: 'https://www.amazon.com/Thinking-Fast-Slow-Daniel-Kahneman/dp/0374533555',
        resource2Name: 'Harvard Business Review - Decision Making',
        resource2Url: 'https://hbr.org/topic/decision-making',
        resource3Name: 'Behavioral Economics Guide',
        resource3Url: 'https://www.behavioraleconomics.com/',
        relatedEpisodes: [2, 3],
        category: 'Psychology',
        category2: 'Business Strategy',
        audioUrl: 'https://example.com/episode1.mp3',
        duration: '45 min',
        advertisingImage: 'https://public.readdy.ai/ai/img_res/4e3929344f845e8be0805c5044bf0af8.jpg',
        date: '2024-01-15',
        viewCount: 0
      },
      {
        id: 2,
        title: 'Craft Distilling: From Passion to Profit',
        description: 'Building a successful spirits business from the ground up, exploring the intersection of craftsmanship and business strategy with master distillers.',
        guest: 'Jake Morrison',
        guestLinkedin: 'https://linkedin.com/in/jake-morrison',
        guest2: 'Maria Santos',
        guestLinkedin2: 'https://linkedin.com/in/maria-santos',
        notes: 'Distillery startup strategies and premium brand development',
        showNotes: '• Welcome and introductions\n• The craft distilling industry overview\n• Starting a distillery business\n• Equipment and facility considerations\n• Brand development and marketing\n• Distribution challenges and solutions\n• Future of craft spirits',
        transcript: 'Today on the DBD Podcast, we are joined by Jake Morrison and Maria Santos, two successful craft distillers who have built thriving businesses...',
        resource1Name: 'Craft Distilling Business Guide',
        resource1Url: 'https://www.distilling.com/business-guide',
        resource2Name: 'American Craft Spirits Association',
        resource2Url: 'https://www.americancraftspirits.org/',
        resource3Name: 'Distillery Equipment Resources',
        resource3Url: 'https://www.distilleryequipment.com/',
        relatedEpisodes: [1, 3],
        category: 'Spirits Industry',
        category2: 'Business Strategy',
        audioUrl: 'https://example.com/episode2.mp3',
        duration: '52 min',
        advertisingImage: 'https://readdy.ai/api/search-image?query=Craft%20distillery%20with%20copper%20stills%20and%20wooden%20barrels%20premium%20spirits%20production%20artisanal%20whiskey%20making%20traditional%20distillation%20equipment%20master%20distiller%20workspace%20elegant%20alcohol%20production%20facility%20professional%20craft%20business&width=400&height=300&seq=episode-2&orientation=landscape',
        date: '2024-01-08',
        viewCount: 0
      },
      {
        id: 3,
        title: 'Community Building in the Digital Age',
        description: 'Strategies for creating authentic online communities and social entrepreneurship initiatives that drive sustainable economic opportunities.',
        guest: 'Maria Gonzalez',
        guestLinkedin: 'https://linkedin.com/in/maria-gonzalez',
        notes: 'Digital community strategies and social impact business models',
        showNotes: '• Introduction to digital community building\n• Authentic engagement strategies\n• Platform selection and management\n• Monetization without losing authenticity\n• Social entrepreneurship models\n• Measuring community impact\n• Future trends in community building',
        transcript: 'Welcome back to the DBD Podcast. I am excited to have Maria Gonzalez with us today to discuss community building in our digital world...',
        resource1Name: 'Community Building Playbook',
        resource1Url: 'https://www.community.club/playbook',
        resource2Name: 'Social Impact Measurement Tools',
        resource2Url: 'https://www.socialimpactnavigator.org/',
        resource3Name: 'Digital Community Platforms Comparison',
        resource3Url: 'https://www.communityplatforms.guide/',
        relatedEpisodes: [1, 2],
        category: 'Community',
        category2: 'Technology',
        audioUrl: 'https://example.com/episode3.mp3',
        duration: '48 min',
        advertisingImage: 'https://readdy.ai/api/search-image?query=Digital%20community%20building%20modern%20workspace%20with%20laptops%20and%20collaboration%20tools%20online%20networking%20social%20entrepreneurship%20coworking%20space%20technology%20startup%20environment%20collaborative%20business%20meeting%20community%20development&width=400&height=300&seq=episode-3&orientation=landscape',
        date: '2024-01-01',
        viewCount: 0
      }
    ];
  }

  private getDefaultCategories(): Category[] {
    return [
      { id: 1, name: 'Psychology', description: 'Business psychology and behavioral insights', createdDate: '2024-01-01' },
      { id: 2, name: 'Spirits Industry', description: 'Distillation, brewing, and spirits business', createdDate: '2024-01-01' },
      { id: 3, name: 'Community', description: 'Community building and social entrepreneurship', createdDate: '2024-01-01' },
      { id: 4, name: 'Technology', description: 'Digital transformation and innovation', createdDate: '2024-01-01' },
      { id: 5, name: 'Business Strategy', description: 'Strategic planning and business development', createdDate: '2024-01-01' }
    ];
  }

  private getDefaultIndustries(): Industry[] {
    return [
      { id: 1, name: 'Brand Owner', description: 'Companies that own and manage brand portfolios', createdDate: '2024-01-01' },
      { id: 2, name: 'Distributor', description: 'Distribution and logistics companies', createdDate: '2024-01-01' },
      { id: 3, name: 'Consultant', description: 'Business consulting and advisory services', createdDate: '2024-01-01' },
      { id: 4, name: 'Retailer', description: 'Retail and e-commerce businesses', createdDate: '2024-01-01' },
      { id: 5, name: 'Bar/Restaurant', description: 'Hospitality and food service industry', createdDate: '2024-01-01' },
      { id: 6, name: 'Other', description: 'Other industries not listed above', createdDate: '2024-01-01' }
    ];
  }
}

export const database = new Database();

